import { useContext, useMemo } from 'react';

import { I18nContext } from './I18nProvider';
import type { I18nKey, I18nLanguage, I18nNamespace, I18nScopedKey, I18nTranslateFunction } from './types';

type UseTranslationResult<Namespace extends I18nNamespace | undefined> = {
  language: I18nLanguage;
  t: Namespace extends I18nNamespace ? I18nTranslateFunction<I18nScopedKey<Namespace>> : I18nTranslateFunction<I18nKey>;
};

export const useTranslation = <Namespace extends I18nNamespace | undefined = undefined>(
  namespace?: Namespace
): UseTranslationResult<Namespace> => {
  const { language, t } = useContext(I18nContext);

  const scopedTranslate = useMemo(() => {
    if (!namespace) {
      return t;
    }

    const namespacedTranslate: I18nTranslateFunction<I18nScopedKey<Extract<Namespace, I18nNamespace>>> = (
      key,
      options
    ) => {
      return t(`${namespace}.${key}` as I18nKey, options);
    };

    return namespacedTranslate;
  }, [namespace, t]) as UseTranslationResult<Namespace>['t'];

  return {
    language,
    t: scopedTranslate,
  };
};
